import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiBaseOrigin } from '../../../../shared/config/api-origin';
import { createAutonomousAdapter } from '../../adapters/autonomous-http';
import { createConfigurationAdapter } from '../../adapters/configuration-http';
import { createEvaluationCountsAdapter } from '../../adapters/evaluation-counts-http';
import { createFireSignalAdapter } from '../../adapters/fire-signals/http';
import { createForecastAdapter } from '../../adapters/forecasting-http';
import { createMonitoringAdapter, MonitoringReadError } from '../../adapters/http';
import { autonomousKpis, type AutonomousPort } from '../../consumer/autonomous';
import type {
  WorkspaceLocation,
  WorkspaceMode,
  WorkspaceProjection,
  WorkspaceResearchData,
} from '../../consumer/command-workspace-contract';
import { readConfigurationSnapshot } from '../../consumer/configuration-state';
import { riskHref, type RiskPageProps } from '../../consumer/workbench';
// Integrated consumer projection and navigation contract (VS08-FE-RECONCILE-001).
import { PlatformHeader } from '../../../../shared/platform/PlatformHeader';
import { Modal } from '../../../../shared/ui/Primitives';
import '../../../overview/ui/overview.css';
import {
  parseWorkspaceLocation,
  projectMonitoring,
  projectResearch,
  workspaceHref,
} from '../../consumer/command-workspace';
import { parseBbox, signalHref, zoomBbox } from '../../consumer/fire-signals/contract';
import { RiskDesktopWorkspace } from '../desktop-workspace/RiskDesktopWorkspace';
import { RegionalMonitoringPage } from '../regional-monitoring/RegionalMonitoringPage';
import { shouldUseRegionalMonitoring } from '../../consumer/regional-monitoring';
import {
  AttentionContext,
  formatRecordTime,
  historicalDateLabel,
  KIND_LABELS,
  ResearchCanvas,
  selectedItem,
  TopologyCanvas,
} from './WorkspaceCanvas';
import './command-workspace.css';
import {
  createWorkspaceReadScheduler,
  readResearchScope,
  researchQuery,
  useWorkspaceRead,
} from './workspace-reads';

export const INITIAL_SOURCE_LAYERS: Readonly<Record<WorkspaceMode, boolean>> = {
  monitor: false,
  'fire-signals': true,
};
export function toggleSourceLayer(
  layers: Readonly<Record<WorkspaceMode, boolean>>,
  mode: WorkspaceMode,
): Record<WorkspaceMode, boolean> {
  return { ...layers, [mode]: !layers[mode] };
}
const EMPTY: WorkspaceProjection = {
  kpis: [],
  items: [],
  areas: [],
  total: null,
  current: false,
  context: '',
  bbox: null,
};
export function bindResearchPresentation(
  projection: WorkspaceProjection,
  search: string,
  historical: boolean,
): WorkspaceProjection {
  if (!historical) return projection;
  const scope = researchQuery(search);
  return {
    ...projection,
    bbox: parseBbox(new URLSearchParams(scope).get('bbox')) ?? projection.bbox,
    items: projection.items.map((item) => ({
      ...item,
      href: item.candidate ? signalHref(scope, item.candidate.candidateId) : item.href,
    })),
  };
}
export function recordOrigin(
  item: WorkspaceProjection['items'][number],
  projection: WorkspaceProjection,
  allAreas: { id: string; name: string }[],
): string {
  const area =
    allAreas.find((entry) => entry.id === item.areaId)?.name ??
    projection.areas.find((entry) => entry.id === item.areaId)?.name ??
    item.areaId;
  if (item.candidate)
    return `${area} · ${item.candidate.satellite.satellite} / ${item.candidate.satellite.instrument}`;
  const sources = item.sourceIds.map(
    (id) =>
      projection.areas.flatMap((entry) => entry.sources).find((source) => source.id === id)?.name ??
      id,
  );
  return `${area} · ${sources.length ? sources.join(' / ') : '来源待核对'}`;
}
function browserSearch() {
  return typeof window === 'undefined' ? '' : window.location.search;
}
export function WorkspaceContent({
  projection,
  location,
  research,
  search,
  update,
  retry,
  errors = [],
  allAreas = projection.areas,
}: {
  projection: WorkspaceProjection;
  location: WorkspaceLocation;
  research: WorkspaceResearchData | null;
  search: string;
  update: (patch: Partial<WorkspaceLocation>, replace?: boolean) => void;
  retry: () => void;
  errors?: string[];
  allAreas?: { id: string; name: string }[];
}) {
  const [sourceLayers, setSourceLayers] = useState(() => ({ ...INITIAL_SOURCE_LAYERS }));
  const sources = sourceLayers[location.mode];
  const [zoom, setZoom] = useState(1);
  const [info, setInfo] = useState(false);
  const historical = location.mode === 'fire-signals';
  const presented = bindResearchPresentation(projection, search, historical);
  const selection = selectedItem(presented.items, location.selected);
  const filtered = presented.items.filter(
    (item) => location.filter === 'all' || item.kind === location.filter,
  );
  const visibleProjection = {
    ...presented,
    items: filtered,
    areas: presented.areas.map((area) => ({
      ...area,
      attentionIds: area.attentionIds.filter((id) => filtered.some((item) => item.id === id)),
    })),
  };
  const filters = historical
    ? (['all', 'linked', 'unlinked'] as const)
    : (['all', 'warning', 'forecast'] as const);
  const completeHref = historical
    ? signalHref(researchQuery(search))
    : riskHref('', location.areaId, { view: 'records' });
  const researchAreas = research?.summary.areas ?? [];
  function changeZoom(factor: number) {
    if (historical && presented.bbox) {
      const query = new URLSearchParams(search);
      query.set('bbox', zoomBbox(presented.bbox!, factor).join(','));
      query.delete('selected');
      window.history.pushState(null, '', `${window.location.pathname}?${query}`);
      window.dispatchEvent(new PopStateEvent('popstate'));
    } else setZoom((value) => Math.min(1.6, Math.max(0.7, value / factor)));
  }
  return (
    <div className="cw-page" data-vs08-workspace data-mode={location.mode}>
      <PlatformHeader activePage="monitoring" monitoringMode={location.mode} />
      <main id="nm-main" tabIndex={-1} className="cw-main">
        <header className="cw-heading">
          <div>
            <p className="cw-breadcrumb">常态运行 / 风险与预警</p>
            <div className="cw-title">
              <h1 data-vs08-title>风险与预警</h1>
              <span>空间态势工作台</span>
            </div>
          </div>
          <div className="cw-heading-tools">
            <div className="cw-mode" role="group" aria-label="数据工作模式">
              {(['monitor', 'fire-signals'] as const).map((mode) => (
                <button
                  type="button"
                  key={mode}
                  data-vs08-mode={mode}
                  aria-pressed={mode === location.mode}
                  onClick={() => {
                    setZoom(1);
                    update({ mode, selected: '', filter: 'all' });
                  }}
                >
                  {mode === 'monitor' ? '监测态势' : '卫星历史信号'}
                </button>
              ))}
            </div>
            <button type="button" className="cw-outline cw-info" onClick={() => setInfo(true)}>
              数据与模式说明 ↗
            </button>
          </div>
        </header>
        <p className="cw-mode-note" role="note">
          <span>{historical ? '历史 · 非实时 · 火情未核实' : '模拟 · 非运营'}</span>
          {historical && (
            <time className="cw-mode-date">{historicalDateLabel(presented.items)}</time>
          )}
        </p>
        <section className="cw-kpis" aria-label="关键状态指标">
          {(projection.kpis.length
            ? projection.kpis
            : ['观测预警', '提前关注', '来源覆盖', '待核对'].map((label, i) => ({
                key: `loading-${i}`,
                label,
                value: null,
                note: '等待后端读取',
              }))
          ).map((kpi, i) => (
            <article
              key={kpi.key}
              className={i === 0 ? 'cw-kpi cw-kpi-first' : 'cw-kpi'}
              data-vs08-kpi={kpi.key}
            >
              <div>
                <span>{kpi.label}</span>
                <strong
                  data-value={kpi.value ?? 'UNKNOWN'}
                  aria-describedby={`cw-kpi-definition-${kpi.key}`}
                >
                  {kpi.value ?? '—'}
                </strong>
                <span className="cw-sr-only" id={`cw-kpi-definition-${kpi.key}`}>
                  {kpi.note}
                </span>
              </div>
              <span className="cw-kpi-icon" aria-hidden="true">
                {['◉', '↗', '◎', '◷'][i]}
              </span>
            </article>
          ))}
        </section>
        {!location.valid && (
          <div role="alert" className="cw-notice">
            400 · 工作台链接无效，请检查模式、区域或筛选条件。
          </div>
        )}
        {errors.length > 0 && (
          <div role="alert" className="cw-notice">
            <span>{errors.join(' / ')} · 当前资料待核对</span>
            <button type="button" onClick={retry}>
              重新读取
            </button>
          </div>
        )}
        <section className="cw-board">
          <section
            className="cw-canvas-card"
            aria-label={historical ? '卫星历史地理态势' : '区域监测态势'}
          >
            <header className="cw-canvas-heading">
              <div>
                <h2>{historical ? '卫星热异常分布' : '区域监测态势'}</h2>
                <span className="cw-chip">{historical ? 'EPSG:4326' : '区域 / 来源拓扑'}</span>
              </div>
              <label className="cw-area-label">
                <span className="cw-sr-only">{historical ? '研究区域' : '监测区域'}</span>
                <select
                  data-vs08-area
                  value={historical ? location.areaRef : location.areaId}
                  onChange={(event) =>
                    update(
                      historical
                        ? { areaRef: event.target.value, selected: '' }
                        : { areaId: event.target.value, selected: '' },
                    )
                  }
                >
                  <option value="">{historical ? '全部研究区' : '全部监测区'}</option>
                  {historical
                    ? researchAreas.map((area) => (
                        <option key={area.areaRef} value={area.areaRef}>
                          {area.name}
                        </option>
                      ))
                    : allAreas.map((area) => (
                        <option key={area.id} value={area.id}>
                          {area.name}
                        </option>
                      ))}
                  {(historical ? location.areaRef : location.areaId) &&
                    !(historical
                      ? researchAreas.some((area) => area.areaRef === location.areaRef)
                      : allAreas.some((area) => area.id === location.areaId)) && (
                      <option value={historical ? location.areaRef : location.areaId}>
                        {historical ? location.areaRef : location.areaId}
                      </option>
                    )}
                </select>
              </label>
            </header>
            <div className="cw-canvas" data-vs08-canvas>
              <div className="cw-canvas-toolbar">
                <button
                  type="button"
                  className="cw-small"
                  aria-pressed={sources}
                  data-vs08-source-layer
                  title={
                    historical ? '显示或隐藏真实 NOAA 气象站' : '按需显示全部来源名称、状态与 epoch'
                  }
                  onClick={() =>
                    setSourceLayers((layers) => toggleSourceLayer(layers, location.mode))
                  }
                >
                  ◎ 来源图层
                </button>
                <button
                  type="button"
                  className="cw-small"
                  onClick={() => {
                    setZoom(1);
                    if (historical) {
                      const query = new URLSearchParams(search);
                      query.delete('bbox');
                      window.history.pushState(null, '', `${window.location.pathname}?${query}`);
                      window.dispatchEvent(new PopStateEvent('popstate'));
                    }
                  }}
                >
                  ↺ 重置视图
                </button>
              </div>
              <div className="cw-canvas-viewport">
                <div
                  className="cw-canvas-transform"
                  style={{ transform: `scale(${historical ? 1 : zoom})` }}
                >
                  {historical ? (
                    <ResearchCanvas
                      projection={visibleProjection}
                      data={research}
                      selected={selection?.id ?? ''}
                      select={(selected) => update({ selected })}
                      sources={sources}
                    />
                  ) : (
                    <TopologyCanvas
                      projection={visibleProjection}
                      selected={selection?.id ?? ''}
                      select={(selected) => update({ selected })}
                      scope={(areaId) => update({ areaId, selected: '' })}
                      sources={sources}
                    />
                  )}
                </div>
              </div>
              <div className="cw-zoom">
                <button type="button" aria-label="放大画布" onClick={() => changeZoom(0.5)}>
                  ＋
                </button>
                <button type="button" aria-label="缩小画布" onClick={() => changeZoom(2)}>
                  −
                </button>
              </div>
              <div className="cw-legend">
                {historical ? (
                  <>
                    <span>
                      <i className="cw-linked" />
                      已关联天气
                    </span>
                    <span>
                      <i className="cw-unlinked" />
                      未关联天气
                    </span>
                    <span>▲ NOAA 气象站</span>
                  </>
                ) : (
                  <>
                    <span>
                      <i className="cw-warning" />
                      观测预警
                    </span>
                    <span>
                      <i className="cw-forecast" />
                      预测关注
                    </span>
                    {sources && (
                      <span>
                        <i className="cw-source-dot" />
                        监测来源
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
            <footer className="cw-canvas-footer">
              <span>
                {historical
                  ? 'NASA FIRMS / NOAA NCEI · 经纬度格网，无底图'
                  : '配置关系 · 无地理坐标'}
              </span>
              {historical && <span>像元中心不表示燃烧范围</span>}
            </footer>
          </section>
          <aside className="cw-side" aria-label="需要关注">
            <header className="cw-side-heading">
              <h2>需要关注</h2>
              <small>按最近变化排序</small>
            </header>
            <div className="cw-filters" aria-label="关注类型">
              {filters.map((filter) => (
                <button
                  type="button"
                  key={filter}
                  data-vs08-filter={filter}
                  aria-pressed={location.filter === filter}
                  onClick={() => update({ filter })}
                >
                  {filter === 'all' ? '全部' : KIND_LABELS[filter]}
                </button>
              ))}
            </div>
            <div className="cw-queue" data-vs08-queue>
              {filtered.slice(0, 5).map((item) => (
                <button
                  type="button"
                  className={`cw-record cw-${item.kind}`}
                  key={item.id}
                  data-vs08-item-id={item.id}
                  data-kind={item.kind}
                  data-time={item.time}
                  aria-pressed={item.id === selection?.id}
                  onClick={() => update({ selected: item.id })}
                >
                  <i aria-hidden="true" />
                  <span className="cw-record-copy">
                    <strong>{item.title}</strong>
                    <small className="cw-record-origin">
                      {recordOrigin(item, projection, allAreas)}
                    </small>
                    <span className="cw-record-meta">
                      <time dateTime={item.time} title={item.time}>
                        {formatRecordTime(item.time, historical)}
                      </time>
                      <em title={item.status}>{item.status}</em>
                    </span>
                  </span>
                </button>
              ))}
              {!filtered.length && (
                <div className="cw-empty">
                  暂无匹配的关注记录
                  <br />
                  <small>无记录不表示区域安全</small>
                </div>
              )}
            </div>
            <AttentionContext item={selection} missing={Boolean(location.selected) && !selection} />
            <footer className="cw-side-footer">
              <small>
                {filtered.length > 5
                  ? `预览 5 / ${filtered.length} 条`
                  : `当前 ${filtered.length} 条`}
              </small>
              <a data-vs08-all href={completeHref}>
                查看全部记录 →
              </a>
            </footer>
          </aside>
        </section>
        <nav className="cw-secondary" aria-label="管理与评估">
          <span className="cw-sr-only">{projection.context}</span>
          <a href={riskHref('/network', location.areaId)}>区域与来源 ↗</a>
          <a href={riskHref('/evaluation', location.areaId)}>模型效果与回验 ↗</a>
          <button type="button" onClick={retry}>
            刷新读取 ↻
          </button>
        </nav>
        {info && (
          <Modal title="数据与模式说明" close={() => setInfo(false)}>
            <h3>指标定义</h3>
            <dl className="cw-definitions">
              {projection.kpis.map((kpi) => (
                <div key={kpi.key}>
                  <dt>{kpi.label}</dt>
                  <dd>{kpi.note}</dd>
                </div>
              ))}
            </dl>
            <p>
              监测态势使用当前 Java / SQLite
              合成监测数据。观测预警和预测关注具有各自的对象身份、时间与依据；来源状态或缺失值不能证明区域安全。
            </p>
            <p>
              卫星历史信号使用 NASA FIRMS 与 NOAA NCEI
              的固定历史资料。真实经纬度显示在无底图格网；数字标记可展开相邻像元。天气关联是时空背景，不是火灾确认或像元现场天气。
            </p>
            <p>
              仅供合成演示与历史研究，不产生救援任务或行动授权。完整来源、规则与不可变身份请进入记录详情。
            </p>
          </Modal>
        )}
      </main>
    </div>
  );
}
export function CommandWorkspaceView({
  port,
  configurationPort,
  forecastPort,
  countsPort,
  researchPort,
  autonomousPort,
}: Omit<RiskPageProps, 'areaId'> & {
  researchPort: ReturnType<typeof createFireSignalAdapter>;
  autonomousPort?: AutonomousPort;
}) {
  const scheduler = useMemo(() => createWorkspaceReadScheduler(2), []);
  const [search, setSearch] = useState(browserSearch);
  const [desktop, setDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1101px)').matches,
  );
  const [refresh, setRefresh] = useState(0);
  const location = useMemo(() => parseWorkspaceLocation(search), [search]);
  useEffect(() => {
    const changed = () => setSearch(browserSearch());
    window.addEventListener('popstate', changed);
    return () => window.removeEventListener('popstate', changed);
  }, []);
  useEffect(() => {
    const query = window.matchMedia('(min-width: 1101px)');
    const changed = () => setDesktop(query.matches);
    query.addEventListener('change', changed);
    return () => query.removeEventListener('change', changed);
  }, []);
  const update = useCallback((patch: Partial<WorkspaceLocation>, replace = false) => {
    const current = new URLSearchParams(browserSearch());
    const target = new URL(
      workspaceHref(parseWorkspaceLocation(browserSearch()), patch),
      window.location.origin,
    );
    for (const key of ['dataSource', 'demoArea']) {
      if (current.has(key)) target.searchParams.set(key, current.get(key)!);
    }
    target.searchParams.set(
      'view',
      (patch.mode ?? parseWorkspaceLocation(browserSearch()).mode) === 'fire-signals'
        ? 'historical'
        : 'monitoring',
    );
    const href = target.pathname + target.search;
    if (replace) window.history.replaceState(null, '', href);
    else window.history.pushState(null, '', href);
    setSearch(browserSearch());
  }, []);
  const demo = new URLSearchParams(search).get('dataSource') === 'demo';
  const monitor = !demo && location.mode === 'monitor' && location.valid;
  const summaryRead = useMemo(
    () => (monitor ? (signal: AbortSignal) => port.summary(signal) : null),
    [monitor, port],
  );
  const warningRead = useMemo(
    () =>
      monitor
        ? async (signal: AbortSignal) => {
            const filter = {
              areaId: location.areaId,
              state: 'ACTIVE' as const,
              from: '',
              to: '',
              limit: 100,
              offset: 0,
            };
            const first = await port.warnings(filter, signal);
            const items = [...first.items];
            while (items.length < first.total) {
              const page = await port.warnings({ ...filter, offset: items.length }, signal);
              if (!page.items.length || page.total !== first.total || page.offset !== items.length)
                throw new MonitoringReadError('WIRE');
              items.push(...page.items);
            }
            if (
              items.length !== first.total ||
              new Set(items.map((item) => item.warningId)).size !== items.length
            )
              throw new MonitoringReadError('WIRE');
            return { ...first, items };
          }
        : null,
    [monitor, port, location.areaId],
  );
  const forecastRead = useMemo(
    () => (monitor && forecastPort ? (signal: AbortSignal) => forecastPort.summary(signal) : null),
    [monitor, forecastPort],
  );
  const networkRead = useMemo(
    () =>
      monitor && configurationPort
        ? (signal: AbortSignal) => readConfigurationSnapshot(configurationPort, signal)
        : null,
    [monitor, configurationPort],
  );
  const countsRead = useMemo(
    () =>
      monitor && countsPort
        ? (signal: AbortSignal) => countsPort.read(location.areaId, signal)
        : null,
    [monitor, countsPort, location.areaId],
  );
  const autonomousRead = useMemo(
    () =>
      monitor && autonomousPort
        ? (signal: AbortSignal) => autonomousPort.read(location.areaId, signal)
        : null,
    [monitor, autonomousPort, location.areaId],
  );
  const autonomous = useWorkspaceRead(autonomousRead, refresh, 3000, scheduler);
  const researchScope = researchQuery(search);
  const researchRead = useMemo(
    () =>
      !demo && location.mode === 'fire-signals' && location.valid
        ? (signal: AbortSignal) => readResearchScope(researchPort, researchScope, signal)
        : null,
    [demo, location.mode, location.valid, researchPort, researchScope],
  );
  const summary = useWorkspaceRead(summaryRead, refresh, 3000, scheduler),
    warnings = useWorkspaceRead(warningRead, refresh, 3000, scheduler),
    forecast = useWorkspaceRead(forecastRead, refresh, 3000, scheduler),
    network = useWorkspaceRead(networkRead, refresh, 3000, scheduler),
    counts = useWorkspaceRead(countsRead, refresh, 3000, scheduler),
    research = useWorkspaceRead(researchRead, refresh, 0, scheduler);
  const reads = { summary, warnings, forecast, network, counts };
  const baseProjection = monitor
    ? projectMonitoring(reads, location.areaId)
    : research.status === 'READY' && research.data
      ? projectResearch(research.data, location.areaRef)
      : EMPTY;
  const projection =
    monitor && autonomousPort
      ? {
          ...baseProjection,
          kpis: autonomousKpis(autonomous),
          current:
            baseProjection.current &&
            autonomous.status === 'READY' &&
            autonomous.data?.dataStatus === 'READY',
        }
      : baseProjection;
  const errors = (monitor ? Object.values(reads) : [research]).flatMap((state) =>
    state.error ? [state.error] : [],
  );
  useEffect(() => {
    if (!demo && !location.selected && projection.items[0])
      update({ selected: projection.items[0].id }, true);
  }, [demo, location.selected, projection.items, update]);
  if (desktop)
    return (
      <RiskDesktopWorkspace
        projection={bindResearchPresentation(projection, search, location.mode === 'fire-signals')}
        location={location}
        allAreas={(network.data?.configuration.areas ?? []).map((area) => ({
          id: area.areaId,
          name: area.displayName,
        }))}
        search={search}
        research={research.status === 'READY' ? research.data : null}
        update={update}
        retry={() => setRefresh((value) => value + 1)}
        errors={errors}
        autonomous={monitor && autonomousPort ? autonomous : undefined}
        loading={
          !demo &&
          (monitor ? Object.values(reads) : [research]).some((state) => state.status === 'LOADING')
        }
      />
    );
  return (
    <WorkspaceContent
      projection={projection}
      location={location}
      allAreas={(network.data?.configuration.areas ?? []).map((area) => ({
        id: area.areaId,
        name: area.displayName,
      }))}
      search={search}
      research={research.status === 'READY' ? research.data : null}
      update={update}
      retry={() => setRefresh((value) => value + 1)}
      errors={errors}
    />
  );
}
function LegacyCommandWorkspacePage() {
  const ports = useMemo(() => {
    const options = { baseOrigin: apiBaseOrigin() };
    return {
      port: createMonitoringAdapter(options),
      autonomousPort: createAutonomousAdapter(options),
      configurationPort: createConfigurationAdapter(options),
      forecastPort: createForecastAdapter(options),
      countsPort: createEvaluationCountsAdapter(options),
      researchPort: createFireSignalAdapter(),
    };
  }, []);
  return <CommandWorkspaceView {...ports} />;
}
export function CommandWorkspacePage() {
  const [search, setSearch] = useState(browserSearch);
  useEffect(() => {
    const update = () => setSearch(browserSearch());
    window.addEventListener('popstate', update);
    return () => window.removeEventListener('popstate', update);
  }, []);
  return shouldUseRegionalMonitoring(search) ? (
    <RegionalMonitoringPage />
  ) : (
    <LegacyCommandWorkspacePage />
  );
}
